import { AbstractValidatedClass, Joi, ValidatedClass, constraint } from '@verclam/form-validator';
import type StaffMemberView from '@UI/Store/StaffMembers/Views/StaffMemberView';

type StaffMemberFormArgs = {
	firstName: string;
	lastName: string;
	jobFunction: string | null;
	phoneNumber: string | null;
	email: string | null;
	facilityIds: readonly number[];
};

/**
 * `phoneNumber` carries no format constraint on purpose: international numbers, country codes,
 * extensions and separators vary too widely for a pattern to stay correct.
 *
 * `ignoreNullValue` is deliberately absent: `jobFunction` is required and defaults to `null`
 * (`createEmpty()`), so its constraint must run against `null` to be enforced. `phoneNumber` and
 * `email` stay optional by allowing `null` explicitly in their own constraint instead.
 */
@ValidatedClass()
class StaffMemberFormData extends AbstractValidatedClass<StaffMemberFormArgs> {
	@constraint(Joi.string().max(180), () => 'Le prénom ne doit pas dépasser 180 caractères.')
	@constraint(Joi.string().required(), () => 'Le prénom est obligatoire')
	public readonly firstName: string;

	@constraint(Joi.string().max(180), () => 'Le nom ne doit pas dépasser 180 caractères.')
	@constraint(Joi.string().required(), () => 'Le nom est obligatoire')
	public readonly lastName: string;

	@constraint(Joi.string().required(), () => 'La fonction est obligatoire')
	public readonly jobFunction: string | null;

	@constraint(
		Joi.string().max(30).allow('', null),
		() => 'Le numéro de téléphone ne doit pas dépasser 30 caractères.',
	)
	public readonly phoneNumber: string | null;

	@constraint(Joi.string().email({ tlds: false }).allow('', null), () => 'L’adresse courriel est invalide.')
	public readonly email: string | null;

	@constraint(Joi.array().min(1), () => 'Sélectionnez au moins une agence')
	public readonly facilityIds: readonly number[];

	constructor(data: StaffMemberFormArgs) {
		super(data);
		this.firstName = data.firstName;
		this.lastName = data.lastName;
		this.jobFunction = data.jobFunction;
		this.phoneNumber = data.phoneNumber;
		this.email = data.email;
		this.facilityIds = data.facilityIds;
		Object.freeze(this);
	}

	static createEmpty(): StaffMemberFormData {
		return new StaffMemberFormData({
			firstName: '',
			lastName: '',
			jobFunction: null,
			phoneNumber: null,
			email: null,
			facilityIds: [],
		});
	}

	static fromView(staffMember: StaffMemberView): StaffMemberFormData {
		return new StaffMemberFormData({
			firstName: staffMember.firstName,
			lastName: staffMember.lastName,
			jobFunction: staffMember.jobFunction.value,
			phoneNumber: staffMember.phoneNumber,
			email: staffMember.email,
			facilityIds: staffMember.facilityIds,
		});
	}

	setFirstName(firstName: string): this {
		return this.cloneWith({ firstName } as Partial<StaffMemberFormArgs>);
	}

	setLastName(lastName: string): this {
		return this.cloneWith({ lastName } as Partial<StaffMemberFormArgs>);
	}

	setJobFunction(jobFunction: string | null): this {
		return this.cloneWith({ jobFunction } as Partial<StaffMemberFormArgs>);
	}

	setPhoneNumber(phoneNumber: string): this {
		return this.cloneWith({ phoneNumber } as Partial<StaffMemberFormArgs>);
	}

	setEmail(email: string): this {
		return this.cloneWith({ email } as Partial<StaffMemberFormArgs>);
	}

	setFacilityIds(facilityIds: readonly number[]): this {
		return this.cloneWith({ facilityIds } as Partial<StaffMemberFormArgs>);
	}
}

export default StaffMemberFormData;
